import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  Vector3,
  Texture,
  SceneLoader,
  DynamicTexture,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { assetUrl, UI_PATHS } from '../constants/assetPaths';

/**
 * podAvatar - the shared "pod rig" kit: pod.glb template loading and the
 * circular webcam disc assembly (plane + ring + circle mask + material
 * recipe). Used by AvatarManager for remote players AND by the arrival
 * screen's self-preview (src/ui/arrivalPodPreview.js), so "see yourself as
 * others see you" can never drift from the in-world look.
 *
 * Deliberately imports only @babylonjs/core, the glTF loader side effect,
 * and assetPaths - never AvatarManager (which would drag @babylonjs/gui,
 * UpdateManager, and the video allocator into the arrival chunk graph).
 */

/** Local Y offset the pod clone sits at under its avatar root. */
export const POD_CLONE_OFFSET_Y = -2.0;

// Per-scene resources, keyed by scene so a scene switch naturally starts
// fresh (old scene disposal tears down the template/texture; the WeakMap
// entry is then GC'd).

/** scene -> Promise<AbstractMesh> resolving to a hidden pod template to clone. */
const podTemplateCache = new WeakMap();

/** scene -> one shared standby Texture for every webcam disc without a live feed. */
const standbyTextureCache = new WeakMap();

/**
 * Load pod.glb once per scene and keep it as a disabled template. Every avatar
 * clones it (clones share geometry - one download, one GPU upload) instead of
 * re-importing the model on each join. Mirrors DroneModelManager's pattern.
 * @param {import('@babylonjs/core').Scene} scene
 * @returns {Promise<import('@babylonjs/core').AbstractMesh>}
 */
export function loadPodTemplate(scene) {
  let p = podTemplateCache.get(scene);
  if (!p) {
    p = SceneLoader.ImportMeshAsync(
      '',
      assetUrl('/assets/models/characters/'),
      'pod.glb',
      scene,
    ).then((result) => {
      const templateRoot = result.meshes[0];
      result.meshes.forEach((mesh) => {
        mesh.isPickable = false;
        if (mesh.material) {
          if (mesh.material.name && mesh.material.name.startsWith('Material #63')) {
            mesh.isVisible = false;
          } else {
            const mat = /** @type {import('@babylonjs/core').PBRMaterial} */ (mesh.material);
            mat.reflectionTexture = null;
            if (mat.albedoColor) mat.albedoColor = new Color3(1, 1, 1);
            if (mat.roughness !== undefined) mat.roughness = 1;
            if (mat.metallic !== undefined) mat.metallic = 0;
            if (mat.environmentIntensity !== undefined) mat.environmentIntensity = 0;
            mat.freeze();
          }
        }
      });
      // Keep the source hidden; avatars clone it into enabled hierarchies.
      templateRoot.setEnabled(false);
      return templateRoot;
    });
    podTemplateCache.set(scene, p);
  }
  return p;
}

/**
 * The shared per-scene standby texture (over-cap / out-of-range / video not
 * arrived / camera off). One texture serves every disc - never dispose it per
 * record.
 * @param {import('@babylonjs/core').Scene} scene
 */
export function getStandbyTexture(scene) {
  let t = standbyTextureCache.get(scene);
  if (!t) {
    t = new Texture(UI_PATHS.AVATARS.STANDBY, scene);
    t.uScale = -1;
    standbyTextureCache.set(scene, t);
  }
  return t;
}

/**
 * Build the circular webcam disc assembly parented to `parent`: the square
 * webcam plane, the ring behind it, and the circle-mask DynamicTexture that
 * rounds the plane via opacityTexture.
 * @param {import('@babylonjs/core').Scene} scene
 * @param {import('@babylonjs/core').TransformNode} parent
 * @param {string|number} nameSuffix appended to mesh/texture names (actorNr in-world)
 * @returns {{plane: import('@babylonjs/core').Mesh, ring: import('@babylonjs/core').Mesh, circleMask: DynamicTexture}}
 */
export function createWebcamDisc(scene, parent, nameSuffix) {
  const webcamSize = 2.2;
  const plane = MeshBuilder.CreatePlane(
    'avatarWebcam_' + nameSuffix,
    { width: webcamSize, height: webcamSize },
    scene,
  );
  plane.parent = parent;
  plane.position = new Vector3(0, -0.6, 1.2);
  plane.isPickable = false;

  const maskRes = 256;
  const circleMask = new DynamicTexture('webcamCircleMask_' + nameSuffix, maskRes, scene, false);
  const ctx = circleMask.getContext();
  ctx.clearRect(0, 0, maskRes, maskRes);
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(maskRes / 2, maskRes / 2, maskRes / 2, 0, Math.PI * 2);
  ctx.fill();
  circleMask.update();
  circleMask.hasAlpha = true;

  const ringSize = webcamSize + 0.06;
  const ring = MeshBuilder.CreateDisc(
    'avatarWebcamRing_' + nameSuffix,
    { radius: ringSize / 2, tessellation: 48 },
    scene,
  );
  ring.parent = parent;
  ring.position = new Vector3(0, -0.6, 1.19);
  ring.isPickable = false;
  const ringMat = new StandardMaterial('avatarRingMat_' + nameSuffix, scene);
  ringMat.diffuseColor = new Color3(0.9, 0.9, 0.9);
  ringMat.emissiveColor = new Color3(0.4, 0.4, 0.4);
  ringMat.backFaceCulling = false;
  ring.material = ringMat;

  return { plane, ring, circleMask };
}

/**
 * The webcam-disc material recipe (standby image and live video alike):
 * unlit - the texture drives diffuse AND emissive - rounded by the circle
 * mask, visible from both sides.
 * @param {import('@babylonjs/core').Scene} scene
 * @param {string} name
 * @param {import('@babylonjs/core').BaseTexture} texture
 * @param {DynamicTexture} circleMask
 * @returns {StandardMaterial}
 */
export function createDiscMaterial(scene, name, texture, circleMask) {
  const mat = new StandardMaterial(name, scene);
  mat.diffuseTexture = texture;
  mat.emissiveTexture = texture;
  mat.emissiveColor = new Color3(1, 1, 1);
  mat.opacityTexture = circleMask;
  mat.backFaceCulling = false;
  return mat;
}
